<script setup lang="ts">
import data from '~/data/pages/news/mocks'
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { WidgetNewsCategoriesSidebar } from '~/components/widget/news-categories-sidebar'
import { WidgetTrendingNews } from '~/components/widget/trending-news'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { WidgetSeoContent } from '~/components/widget/seo'
import { WidgetFaqList } from '~/components/widget/faq-list'
import type { IResponseData } from '~/types'
import type { INewsCategory } from '~/components/entity/news-category'
import type { IUser } from '~/components/entity/user'
import { WidgetSidebarNews } from '~/components/widget/sidebar-news'
import { WidgetNewsListMeiliSearch } from '~/components/widget/news-list-meili-search'
import { WidgetCalendar } from '~/components/widget/Calendar'
import { WidgetAuthor } from '~/components/widget/author'

const route = useRoute()
const { t } = useI18n()
const title = t(data.title)
const { pathWithoutLocale } = useLocalizedPath()

// ContentLayout switches to the two-column grid at `xl`; below it the sidebar
// drops under the content, so the ad is moved above the news list.
// Rendered in one place only, so Revive never counts an impression for a hidden banner.
const isSidebarVisible = useIsSidebarVisible()

const { data: newsCategories } = await useApi<IResponseData<INewsCategory[]>>(
  () => useUrlWithParams('/news_categories'),
  {
    key: `news.categories.breadcrumbs`
  }
)

const authorParam = computed(() => {
  const raw = route.params.author
  const param = Array.isArray(raw) ? raw[0] : raw
  return param && param.trim() ? param : null
})

const categoryParam = computed(() => {
  const raw = route.params.category
  const param = Array.isArray(raw) ? raw[0] : raw
  return param && param.trim() ? param : null
})

const tagParam = computed(() => {
  const raw = route.params.tag
  const param = Array.isArray(raw) ? raw[0] : raw
  return param && param.trim() ? param : null
})

const dateParam = computed(() => {
  const { year, month, day } = route.params
  if (year && month && day) {
    return { year: String(year), month: String(month), day: String(day) }
  }
  return null
})

const shouldFetchTag = computed(() => !!tagParam.value)

const { data: tagExists, error: tagError } = shouldFetchTag.value
  ? await useApi<{ result: boolean }>(
      () => `/news/check-tag-existence?tag=${encodeURIComponent(tagParam.value)}`,
      {
        key: `news.tag.check.${tagParam.value}`,
        server: true
      }
    )
  : { data: ref(null), error: ref(null) }

const shouldFetchAuthor = computed(() => !!authorParam.value)

const { data: newsAuthors } = shouldFetchAuthor.value
  ? await useApiWith404<IResponseData<IUser>>(
      () => `/users/${authorParam.value}`,
      {
        key: `news.users.breadcrumbs.${authorParam.value}`,
        server: true
      },
      t('common.author_not_found')
    )
  : { data: ref(null) }

const shouldFetchDate = computed(() => !!dateParam.value)

const { data: dateAvailability, error: dateError } = shouldFetchDate.value
  ? await useApi<{ dates: number[] }>(
      () =>
        `/news/dates?month=${Number(dateParam.value!.month)}&year=${Number(dateParam.value!.year)}`,
      {
        key: `news.dates.check.${dateParam.value!.year}-${dateParam.value!.month}`,
        server: true
      }
    )
  : { data: ref(null), error: ref(null) }

watchEffect(() => {
  if (categoryParam.value && newsCategories.value?.data) {
    const matchedCategory = newsCategories.value.data.find(
      (cat) => cat.slug === categoryParam.value
    )
    if (!matchedCategory) {
      showError({
        statusCode: 404,
        statusMessage: t('common.category_not_found')
      })
    }
  }

  if (tagError.value) {
    showError({
      statusCode: tagError.value.statusCode || 500,
      statusMessage: t('common.server_error_try_later')
    })
  } else if (shouldFetchTag.value && tagExists.value && !tagExists.value.result) {
    showError({
      statusCode: 404,
      statusMessage: t('common.tag_not_found')
    })
  }

  if (dateParam.value) {
    if (dateError.value) {
      showError({
        statusCode: 404,
        statusMessage: t('common.no_news')
      })
    } else if (
      dateAvailability.value &&
      !dateAvailability.value.dates.includes(Number(dateParam.value.day))
    ) {
      showError({
        statusCode: 404,
        statusMessage: t('common.no_news')
      })
    }
  }
})

const newsAuthor = computed(() => {
  if (!shouldFetchAuthor.value || !newsAuthors.value?.data) {
    return null
  }
  return newsAuthors.value.data
})

const breadcrumbs = computed(() => {
  const items = [{ title, url: '/news' }]

  const categoryParam = Array.isArray(route.params.category)
    ? route.params.category[0]
    : route.params.category

  const authorParam = Array.isArray(route.params.author)
    ? route.params.author[0]
    : route.params.author

  const tagParam = Array.isArray(route.params.tag) ? route.params.tag[0] : route.params.tag

  const { year, month, day } = route.params

  if (categoryParam && newsCategories.value?.data?.length) {
    const matchedCategory = newsCategories.value.data.find((cat) => cat.slug === categoryParam)
    if (matchedCategory) {
      items.push({ title: matchedCategory.title, url: route.path })
    }
  } else if (authorParam && newsAuthors.value?.data.id) {
    if (newsAuthor.value) {
      items.push({ title: t('common.authors'), url: '/authors' })
      items.push({ title: newsAuthor.value.fullname, url: route.path })
    }
  } else if (tagParam) {
    items.push({ title: tagParam, url: route.path })
  } else if (year && month && day) {
    const formatted = `${day}.${month}.${year}`
    items.push({ title: formatted, url: route.path })
  }

  return items
})

const seoContext = computed(() => {
  const { year, month, day } = route.params
  const tagParam = Array.isArray(route.params.tag) ? route.params.tag[0] : route.params.tag

  if (newsAuthor.value) {
    const authorName = newsAuthor.value.fullname || t('common.author')
    return {
      title: t('page.news.seo.author.title', { authorName }),
      description: t('page.news.seo.author.description', { authorName }),
      ogTitle: t('page.news.seo.author.ogTitle', { authorName }),
      ogDescription: t('page.news.seo.author.ogDescription', { authorName })
    }
  }

  if (tagParam) {
    return {
      title: t('page.news.seo.tag.title', { tagParam }),
      description: t('page.news.seo.tag.description', { tagParam }),
      ogTitle: t('page.news.seo.tag.ogTitle', { tagParam }),
      ogDescription: t('page.news.seo.tag.ogDescription', { tagParam })
    }
  } else if (year && month && day) {
    const date = `${day}.${month}.${year}`
    return {
      title: t('page.news.seo.date.title', { date }),
      description: t('page.news.seo.date.description', { date }),
      ogTitle: t('page.news.seo.date.ogTitle', { date }),
      ogDescription: t('page.news.seo.date.ogDescription', { date })
    }
  }

  return null
})

watchEffect(() => {
  const seo = seoContext.value
  if (seo) {
    useSeoMeta({
      title: `${seo.title} ${t('seo.suffix.banks_kyrgyzstan')}`,
      description: seo.description,
      ogTitle: seo.ogTitle,
      ogDescription: seo.ogDescription,
      twitterTitle: seo.ogTitle,
      twitterDescription: seo.ogDescription
    })
  }
})

const {
  public: { siteUrl }
} = useRuntimeConfig()
useHead({
  link: [{ rel: 'alternate', type: 'application/rss+xml', href: `${siteUrl}/rss` }]
})

const tagsOrDateSeoHelper = computed(() => {
  const { year, month, day } = route.params
  const tagParam = Array.isArray(route.params.tag) ? route.params.tag[0] : route.params.tag

  if (newsAuthor.value) {
    return newsAuthor.value.fullname || t('common.author')
  }

  if (tagParam) {
    return tagParam
  } else if (year && month && day) {
    return `${day}.${month}.${year}`
  }

  return ''
})
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetAuthor v-if="newsAuthor" :author="newsAuthor" />
            <WidgetTrendingNews />
            <ClientOnly>
              <WidgetReviveAd v-if="isSidebarVisible" zoneid="27" class="overflow-hidden rounded-lg" />
            </ClientOnly>
            <WidgetNewsCategoriesSidebar />
            <WidgetSidebarNews
              v-if="useCleanRouteName(route.name) === 'news' && !route.query.page"
              :heading="$t('page.news.kyrgyz_news_heading')"
              path="/news"
              :query-params="{
                per_page: '5',
                'filter[category]': 'kyrgyzcha'
              }"
              variant="white"
            />
            <WidgetCalendar />
          </aside>
        </template>
        <div class="space-y-5" role="main">
          <WidgetSeoContent
            position="upper"
            :variables="{ news_tag_or_date: tagsOrDateSeoHelper }"
          />
          <ClientOnly>
            <WidgetReviveAd v-if="!isSidebarVisible" zoneid="27" class="overflow-hidden rounded-lg" />
            <template #fallback>
              <!-- Keeps the banner space in the SSR markup, so the news list does not jump on hydration -->
              <div class="HideWithSidebar aspect-[365/250] max-h-[250px]" />
            </template>
          </ClientOnly>
          <WidgetNewsListMeiliSearch :eager-load-count="6" />
          <WidgetSeoContent position="bottom" />
          <WidgetFaqList
            :query-params="{
              'filter[faqable_url]': pathWithoutLocale
            }"
          />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
